import { forwardRef } from 'react';
import {
  composeRenderProps,
  Button as AriaButton,
  ButtonProps as AriaButtonProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { focusRing } from 'src/utils';
import LoadingIcon from 'src/assets/icons/loader.svg';

import type { IconProps, ColorVariant, SizeExtended } from 'src/types/common';

export const buttonStyles = tv({
  extend: focusRing,
  base: 'inline-flex items-center font-semibold transition flex-shrink-0',
  variants: {
    variant: {
      surface: 'bg-surface-alt text-surface-75 hover:bg-surface-alt-hover',
      'surface-dark': 'bg-surface text-surface-75 hover:bg-surface-hover',
      primary:
        'bg-primary text-primary-75 hover:bg-primary-hover border border-primary-30',
      // A theme whose secondary is too light for white text sets
      // --secondary-button-text (ASU dark mode does); otherwise it stays white.
      secondary:
        'bg-secondary text-[color:var(--secondary-button-text,#fff)] hover:bg-secondary-hover',
      success: 'bg-green text-white hover:bg-green-hover',
      danger: 'bg-red text-white hover:bg-red-hover',
      warning: 'bg-yellow text-yellow-75 hover:bg-yellow-hover',
      ai: 'bg-ai text-white hover:bg-ai-hover',
    },
    size: {
      small: 'text-sm px-2.5 py-0.5 h-8 gap-1.5 rounded',
      medium: 'text-md px-4 py-1 h-11 gap-2.5 rounded-lg',
      large: 'text-base px-6 py-1 h-14 gap-3 rounded-lg',
    },
    isDisabled: {
      true: 'cursor-not-allowed brightness-90',
    },
    borderless: {
      true: 'bg-transparent ring-0',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'medium',
  },
});

// A theme can outline light-fill buttons by setting --<color>-ghost-border
// (the ASU theme does, for 3:1 contrast). Unset, the ring is transparent. It is
// a ring rather than a border so it takes no space, and rather than a shadow so
// it does not replace the focus halo.
export const ghostButton = tv({
  extend: buttonStyles,
  variants: {
    variant: {
      surface:
        'bg-surface-alt-ghost text-surface-alt-ghost-75 hover:bg-surface-alt-ghost-hover disabled:text-surface-alt-ghost-45',
      'surface-dark':
        'bg-surface-ghost text-surface-ghost-75 hover:bg-surface-ghost-hover disabled:text-surface-ghost-45',
      primary:
        'bg-primary-ghost text-primary-ghost-75 hover:bg-primary-ghost-hover disabled:text-primary-ghost-45',
      secondary:
        'bg-secondary-ghost text-secondary-ghost-75 hover:bg-secondary-ghost-hover disabled:text-secondary-ghost-45 ring-1 ring-inset ring-[color:var(--secondary-ghost-border,transparent)]',
      success:
        'bg-green-ghost text-green-ghost-alt-75 hover:bg-green-ghost-hover disabled:text-green-ghost-alt-45 ring-1 ring-inset ring-[color:var(--green-ghost-border,transparent)]',
      danger:
        'bg-red-ghost text-red-ghost-alt-75 hover:bg-red-ghost-hover disabled:text-red-ghost-alt-45 ring-1 ring-inset ring-[color:var(--red-ghost-border,transparent)]',
      warning:
        'bg-yellow-ghost text-yellow-ghost-alt-75 hover:bg-yellow-ghost-hover disabled:text-yellow-ghost-alt-45 ring-1 ring-inset ring-[color:var(--yellow-ghost-border,transparent)]',
      ai: 'bg-ai-ghost text-ai-ghost-alt-75 hover:bg-ai-ghost-hover disabled:text-ai-ghost-alt-745 ring-1 ring-inset ring-[color:var(--ai-ghost-border,transparent)]',
    },
    isDisabled: {
      true: 'brightness-100',
    },
  },
});

export const buttonIcon = tv({
  variants: {
    variant: {
      surface: 'text-surface-alt-75',
      'surface-dark': 'text-surface-75',
      primary: 'text-primary-45',
      secondary: 'text-secondary-45',
      success: 'text-green-45',
      danger: 'text-red-45',
      warning: 'text-yellow-45',
      ai: 'text-ai-45',
    },
    size: {
      small: 'w-4 h-4',
      medium: 'w-5 h-5',
      large: 'w-6 h-6',
    },
    loading: {
      true: 'animate-spin',
    },
  },
});

export const buttonGhostIcon = tv({
  extend: buttonIcon,
  variants: {
    variant: {
      surface: 'text-surface-alt-ghost-45',
      'surface-dark': 'text-surface-ghost-45',
      primary: 'text-primary-ghost-45',
      secondary: 'text-secondary-ghost-45',
      success: 'text-green-ghost-alt-45',
      danger: 'text-red-ghost-alt-45',
      warning: 'text-yellow-45',
      ai: 'text-ai-ghost-alt-45',
    },
  },
});

export interface ButtonProps extends AriaButtonProps {
  variant?: ColorVariant | 'surface-dark';
  size?: SizeExtended;
  ghost?: boolean;
  borderless?: boolean;
  loading?: boolean;
  IconLeft?: IconProps;
  IconRight?: IconProps;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'medium',
      ghost = false,
      IconLeft = () => null,
      IconRight = () => null,
      borderless = false,
      loading = false,
      children,
      ...props
    },
    ref
  ) => {
    const Icon = loading ? LoadingIcon : IconLeft;
    return (
      <AriaButton
        {...props}
        ref={ref}
        aria-disabled={loading || props.isDisabled || undefined}
        className={composeRenderProps(
          props.className,
          (className, renderProps) => {
            const options = {
              ...renderProps,
              variant,
              size,
              borderless,
              className,
            };

            if (ghost) return ghostButton(options);

            return buttonStyles(options);
          }
        )}
      >
        <>
          <Icon
            aria-hidden="true"
            className={
              ghost
                ? buttonGhostIcon({ variant, size, loading })
                : buttonIcon({ variant, size, loading })
            }
          />
          {children}
          <IconRight
            aria-hidden="true"
            className={
              ghost
                ? buttonGhostIcon({ variant, size })
                : buttonIcon({ variant, size })
            }
          />
        </>
      </AriaButton>
    );
  }
);

Button.displayName = 'Button';

export default Button;
